import { CdkVirtualScrollViewport, ScrollingModule } from '@angular/cdk/scrolling';
import { CommonModule, NgComponentOutlet } from '@angular/common';
import { ChangeDetectorRef, Component, computed, Inject, inject, Optional, Signal, Type, ViewChild } from '@angular/core';
import { SvgComponent } from '@app/components/shared/svg/svg.component';
import { JournalStore } from '@app/_stores';
import { JournalSetup } from '@app/_interfaces/journal.interface';
import { EntitySetup } from '@app/_interfaces/feature.interface';
import { SettingsService } from '@app/_services/settings.service';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MAT_BOTTOM_SHEET_DATA  } from '@angular/material/bottom-sheet';
import { DependencyService } from '@app/_services/dependency.service';
import { CacheService } from '@app/_services/cache.service';
import { CustomBottomSheetRef } from '@app/_services/custom-bottom-sheet.service';
// NOTE: JournalBlockComponent is loaded dynamically via NgComponentOutlet to break circular dependency
// journal-block → journal-form → journal-setup-form → journal-entries → journal-block

@Component({
    selector: 'app-journal-entries',
    imports: [
        CommonModule,
        SvgComponent,
        ScrollingModule,
        NgComponentOutlet
    ],
    templateUrl: './journal-entries.component.html',
    styleUrl: './journal-entries.component.scss'
})

export class JournalEntriesComponent {

  @ViewChild(CdkVirtualScrollViewport) viewport!: CdkVirtualScrollViewport;
  readonly journalStore = inject(JournalStore);
  idToSetup: Signal<Record<string, JournalSetup>> = this.journalStore.idToSetup;
  setup: Signal<JournalSetup> = computed(() => {
    return this.journalStore.idToSetup()[this.data.entityId];
  });
  show: string[] = ['date', 'repeat'];
  entitiyRepeatDates: Signal<string[]> = computed(() => {
    const setup = this.setup();
    const dates: string[] = this.ds.getAllDatesOfEntity(setup, 'journal', this.cc.user.uid);
    return dates;
  });
  deletedAtString: Signal<string> = computed(() => {
    const deletedAt = this.setup().deletedAt;
    if (!deletedAt) return '';
    return this.ss.interpolateText('common_deletedAt', { date: this.ss.getFormattedDate(deletedAt, 'd MMM yyyy'), time: this.ss.getFormattedDate(deletedAt, 'h:mm a') });
  });
  isDeleted: Signal<boolean> = computed(() => {
    const deletedAt = this.setup().deletedAt;
    if (!deletedAt) return false;
    return true;
  });

  // Dynamically loaded component to break circular dependency
  journalBlockComponent: Type<any> | null = null;
  public data: { entityId: string };

  constructor(
    public ss: SettingsService,
    @Optional() public dialogRef: MatDialogRef<JournalEntriesComponent> | null,
    @Optional() public bottomSheetRef: CustomBottomSheetRef<JournalEntriesComponent> | null,
    @Optional() @Inject(MAT_DIALOG_DATA) dialogData: { entityId: string } | null,
    @Optional() @Inject(MAT_BOTTOM_SHEET_DATA) bottomSheetData: { entityId: string } | null,
    public ds: DependencyService,
    public cc: CacheService,
    private cdr: ChangeDetectorRef,
  ) {
    this.data = (dialogData || bottomSheetData)!;
    this.loadJournalBlockComponent();
  }

  private async loadJournalBlockComponent() {
    const { JournalBlockComponent } = await import('../journal-block/journal-block.component');
    this.journalBlockComponent = JournalBlockComponent;
    // Trigger change detection after async component load
    this.cdr.detectChanges();
  }

  getJournalBlockInputs(dateString: string): Record<string, any> {
    return {
      entity: this.getEntitySetup(dateString),
      show: this.show,
      dateString: dateString
    };
  }

  getEntitySetup(dateString: string): EntitySetup {
    const setup = this.journalStore.getJournalSetup(this.data.entityId);
    const journal = this.journalStore.journalMap().setups[this.data.entityId]?.[dateString] || null;
    if (!journal) return { ...setup, status: 'none' };
    const status = this.journalStore.getStatus(journal, dateString);
    return {
      ...setup,
      status: status
    };
  }

  trackByDateString(index: number, dateString: string) {
    return dateString;
  };

  closeDialog() {
    if (this.dialogRef) {
      this.dialogRef.close();
    } else if (this.bottomSheetRef) {
      this.bottomSheetRef.dismiss();
    }
  };
}
